import React, { useRef, useEffect, useMemo, useImperativeHandle } from 'react';

import {
    Scrolling,
    ColumnFixing,
    Editing,
    Column,
    LoadPanel,
    Paging,
    RemoteOperations,
    Sorting,
    Summary,
    RowDragging
} from 'devextreme-react/data-grid';
import 'devextreme/dist/css/dx.light.css';

import { StyledDataGrid } from './StyledDataGrid';
import { StyledGlobal } from './StyledGlobal';

import { TDataGrid, TInternalProps, TCommonProps, TActionsProps, TInlineEditProps, TExportProps, TForwardedRef } from 'types/gridDefs';
import { TId } from 'types/rowDefs';
import { DataGridColumn } from '../Columns/DataGrid.Column';

import * as customStoreHelpers from '../helpers/customStoreHelpers';
import * as gridHelpers from '../helpers/gridHelpers';
import * as exportHelpers from '../helpers/exportHelpers';
import * as rowHelpers from '../helpers/rowHelpers';
import * as groupHelpers from '../helpers/groupHelpers';
import * as columnHelpers from '../helpers/columnHelpers';

/* ======================================== */

type TProps = React.PropsWithChildren<TInternalProps & TCommonProps & TActionsProps & TInlineEditProps & TExportProps>;

const defaultIdFieldName = 'id';
const defaultHeight = '100%';
const defaultPageSize = 100;

const loadPanelShadingColour = 'rgba(0,0,0,0.4)'; // TODO use theme to set this consistently across all components
const loadPanelText = 'Loading...';               // TODO localise
const defaultNoDataText = 'No Data Loaded';

/* ======================================== */

/**
 * Base implementation of adserve DataGrid
 * !! Do not export from the library !!
 * @private
 */
export const DataGridBase = React.forwardRef<TForwardedRef, TProps>((props, forwardedRef) => {
    const { data, dataUrl, columns, isFetching, isLoading, loadingMessage, totalCaption, calculateCustomSummary } = props;
    const { idFieldName, disableLoadPanel, className, compact, multiSort, enableActions } = props;
    const { selectedRowId, editSelectedRow, editMode } = props;
    const { onDataLoaded, onRowToggleFavourite, onRowSelect, onRowReorder, onExporting } = props;
    const { downloadExcelButtonId, downloadPdfButtonId } = props;

    const ref = useRef<TDataGrid>(null);       // ref to the datagrid - used for calling methods on the datagrid instance

    const refProps = useRef<TProps>(props);
    useEffect(() => { refProps.current = props; });             // keep a ref to the props - that way they can be used in effect hooks without a dependency trigger

    /* -------------------- */

    // if dynamic data store is defined then use it, otherwise use pre-filled data array
    const customStore = customStoreHelpers.getCustomStore(dataUrl, columns, onDataLoaded);

    const keyExpr = Boolean(dataUrl) ? undefined : idFieldName ?? defaultIdFieldName;
    const noDataText = disableLoadPanel ? '' : defaultNoDataText;

    /* -------------------- */

    // expose these methods to the parent component
    useImperativeHandle(forwardedRef, () => ({
        expandGroupsToIndex: groupHelpers.expandGroupsToIndex(ref.current?.instance),

        selectRow: rowHelpers.selectRow(ref.current?.instance),
        editRow: rowHelpers.editRow(ref.current?.instance),
        getSelectedRowData: rowHelpers.getSelectedRowData(ref.current?.instance),

        showColumn: columnHelpers.showColumn(ref.current?.instance),
        hideColumn: columnHelpers.hideColumn(ref.current?.instance),
        setColumnsVisible: columnHelpers.setColumnsVisible(ref.current?.instance),

        getData: exportHelpers.getData(ref.current?.instance),
        exportToCsv: exportHelpers.exportToCsv(ref.current?.instance),
        exportToExcel: exportHelpers.exportToExcel(ref.current?.instance, columns),
        generateWorkbook: exportHelpers.generateWorkbook(ref.current?.instance, columns),
        saveWorkbook: exportHelpers.saveWorkbook,
        exportToPdf: exportHelpers.exportToPDF(ref.current?.instance)
    }), [ref.current]);

    // loadPanel will be shown when isLoading is set as true (data fetch pending) and while the datagrid is rendering
    useEffect(() => gridHelpers.setLoadPanelForFetching(ref.current, isLoading ?? isFetching, loadPanelText, loadingMessage), [isFetching, isLoading, loadingMessage]);

    // bind a resize observer to the grid instance - this will correct blank rows when the grid size changes within the window
    useEffect(() => gridHelpers.bindResizeObserver(ref.current?.instance), [ref]);

    /* --------------------
    /* Below are some react anti-patterns - preserved here because they were used in earlier implementations
    /* -------------------- */

    // toggle the seleced row in/out of edit mode
    useEffect(() => { rowHelpers.setRowEditMode(ref.current, editSelectedRow, refProps.current.selectedRowId); }, [editSelectedRow]);

    // export grid to Excel
    useEffect(() => {
        if (downloadExcelButtonId) {
            const btn = document.getElementById(downloadExcelButtonId);
            if (btn) {
                btn.addEventListener('click', () =>
                    exportHelpers.exportToExcel(ref.current?.instance, columns)(refProps.current.excelFilename ?? '', refProps.current.excelWorksheetName ?? '')
                );
            }
        }
    }, [downloadExcelButtonId]);

    // export grid to PDF
    useEffect(() => {
        if (downloadPdfButtonId) {
            const btn = document.getElementById(downloadPdfButtonId);
            if (btn) {
                btn.addEventListener('click', () =>
                    exportHelpers.exportToPDF(ref.current?.instance)(refProps.current.pdfFilename ?? '')
                );
            }
        }
    }, [downloadPdfButtonId]);

    /* -------------------- */

    useEffect(() => {
        if (ref.current?.instance) {
            gridHelpers.forceRowRender(ref.current.instance, true);
        }
    }, [columns]);
    const summaryGroupItems = useMemo(() => groupHelpers.getSummaryGroupItems(columns), [columns]);
    const summaryTotalItems = useMemo(() => groupHelpers.getSummaryTotalItems(columns, totalCaption), [columns, totalCaption]);

    const columnsList = useMemo(() => {
        if (columns?.length) {
            return columns.map(col => DataGridColumn(col, compact, onRowToggleFavourite));
        }
        return [];
    }, [columns, compact, onRowToggleFavourite]);

    const isGroup = useMemo(() => props.type === 'group' || props.type === 'groupNoCollapse', [props.type]);

    /* -------------------- */

    // localisation not yet configured - using static text for now - TODO incorporate an i18n library

    return (
        <StyledDataGrid
            ref={ref}                           // reference to the devExpress DataGrid - used for dynamic commands: exports; group expand/collapse
            dataSource={data ?? customStore}
            keyExpr={keyExpr}
            noDataText={noDataText}

            height={defaultHeight}
            rowAlternationEnabled
            hoverStateEnabled
            repaintChangesOnly
            columnAutoWidth
            className={className}
            compact={compact}
            remoteOperations={Boolean(customStore)}

            onExporting={onExporting}

            onRowClick={enableActions ? rowHelpers.handleRowSelect(onRowSelect) : undefined}

            onContentReady={gridHelpers.onContentReady(columns)}
            onContextMenuPreparing={gridHelpers.handleContextMenu}
            onRowExpanding={isGroup ? gridHelpers.handleRowExpandCollapseStart : undefined}
            onRowCollapsing={isGroup ? gridHelpers.handleRowExpandCollapseStart : undefined}
            onRowExpanded={isGroup ? gridHelpers.handleRowExpandCollapseEnd : undefined}
            onRowCollapsed={isGroup ? gridHelpers.handleRowExpandCollapseEnd : undefined}

            onEditCanceling={gridHelpers.handleCancelableEvent(true)}     // called on press Esc while editing - cancel & continue editing (TODO maybe call back to owner to cancel edit?)
            onSaving={gridHelpers.handleCancelableEvent(true)}            // called on press Enter while editing - cancel & continue editing (TODO maybe call back to owner to save edit?)
        >
            <StyledGlobal />

            <Scrolling
                mode='virtual'
                preloadEnabled={true}
                columnRenderingMode='standard'
                rowRenderingMode='virtual'
                scrollByContent={true}
                scrollByThumb={true}
                useNative={true}
            />
            <ColumnFixing
                enabled={true}
            />

            <LoadPanel
                enabled={!disableLoadPanel}
                text={loadPanelText}
                shadingColor={loadPanelShadingColour}
            />

            <Editing
                mode={editMode}
                //allowUpdating={Boolean(editMode)}
                selectTextOnEditStart={true}
            />
            {Boolean(editMode) &&       // suppress the default dxDatagrid edit buttons column
                <Column type='buttons' visible={false} />
            }

            <Sorting
                showSortIndexes={multiSort}
                mode={multiSort ? 'multiple' : 'single'}
            />
            <Paging
                defaultPageSize={defaultPageSize}
                defaultPageIndex={0}
                enabled={true}
            />
            <RemoteOperations
                paging={Boolean(customStore)}
                sorting={Boolean(customStore)}
            />

            {(summaryTotalItems.length || summaryGroupItems.length) &&
                <Summary
                    groupItems={summaryGroupItems}
                    totalItems={summaryTotalItems}
                    skipEmptyValues={true}
                    texts={groupHelpers.summaryTexts}
                    calculateCustomSummary={calculateCustomSummary}
                />
            }

            {onRowReorder &&
                <RowDragging
                    allowReordering={true}
                    autoScroll={true}
                    dropFeedbackMode='push'
                    showDragIcons={false}
                    onReorder={rowHelpers.handleRowReorder(data, onRowReorder) as any}
                />
            }

            {columnsList}
        </StyledDataGrid>
    );
});
